
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../../Icons';
import { DEFAULT_PROPERTY_IMAGE } from '../../../constants';

interface PropertyImageSliderProps {
  images: string[];
  aspectRatio?: string;
  className?: string;
  showButtons?: boolean;
}

/**
 * A high-performance, mobile-optimized image slider for property cards.
 * Renders only the active image to prevent WebKit memory crashes on iOS devices,
 * while providing smooth touch swipe and chevron button navigation.
 */
export const PropertyImageSlider: React.FC<PropertyImageSliderProps> = ({ 
  images, 
  aspectRatio = "aspect-[4/3]",
  className = "",
  showButtons = false
}) => {
  const displayImages = images && images.length > 0 ? images : [DEFAULT_PROPERTY_IMAGE];
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset to first slide if images array reference/length changes meaningfully
  const firstImage = displayImages[0];
  useEffect(() => {
    setCurrentIndex(0);
  }, [firstImage]);

  // Ensure currentIndex stays within bounds
  const safeIndex = Math.min(currentIndex, displayImages.length - 1);
  const currentImageSrc = displayImages[safeIndex] || DEFAULT_PROPERTY_IMAGE;

  const handleNext = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e) e.stopPropagation();
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % displayImages.length);
  }, [displayImages.length]);

  const handlePrev = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
    if (e) e.stopPropagation();
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  }, [displayImages.length]);

  // Touch swipe gesture handling without heavy sub-scrollviews
  const touchCoordsRef = useRef<{ startX: number; startY: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (displayImages.length <= 1) return;
    touchCoordsRef.current = {
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchCoordsRef.current || displayImages.length <= 1) return;
    const deltaX = e.changedTouches[0].clientX - touchCoordsRef.current.startX;
    const deltaY = e.changedTouches[0].clientY - touchCoordsRef.current.startY;
    touchCoordsRef.current = null;

    // Only swipe if horizontal movement is dominant and exceeds threshold
    if (Math.abs(deltaX) > 35 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25) {
      e.stopPropagation();
      if (deltaX < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  return (
    <div 
      className={`relative w-full overflow-hidden select-none group ${aspectRatio} ${className}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <img 
        key={`${safeIndex}-${currentImageSrc}`}
        src={currentImageSrc} 
        alt={`Foto ${safeIndex + 1} de ${displayImages.length}`} 
        className="w-full h-full object-cover transition-opacity duration-200"
        referrerPolicy="no-referrer"
        loading="lazy"
        decoding="async"
        onError={(e) => {
          if (e.currentTarget.src !== DEFAULT_PROPERTY_IMAGE) {
            e.currentTarget.src = DEFAULT_PROPERTY_IMAGE;
          }
        }}
      />
      
      {displayImages.length > 1 && (
        <>
          {showButtons && (
            <>
              <button 
                type="button"
                onClick={handlePrev} 
                className="absolute left-2 top-1/2 -translate-y-1/2 z-[30] p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity active:scale-90"
                aria-label="Foto anterior"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <button 
                type="button"
                onClick={handleNext} 
                className="absolute right-2 top-1/2 -translate-y-1/2 z-[30] p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity active:scale-90"
                aria-label="Siguiente foto"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </>
          )}
          
          {/* Indicator dots with active state */}
          <div className="absolute bottom-2 left-0 right-0 flex justify-center items-center gap-1.5 pointer-events-none z-[10]">
            {displayImages.slice(0, 8).map((_, i) => (
              <div 
                key={i} 
                className={`rounded-full transition-all duration-200 ${
                  i === safeIndex 
                    ? 'w-2 h-2 bg-white shadow-md ring-1 ring-black/30' 
                    : 'w-1.5 h-1.5 bg-white/60 shadow-sm'
                }`} 
              />
            ))}
            {displayImages.length > 8 && (
              <div 
                className={`rounded-full transition-all duration-200 ${
                  safeIndex >= 8 
                    ? 'w-2 h-2 bg-white shadow-md ring-1 ring-black/30' 
                    : 'w-1.5 h-1.5 bg-white/40 shadow-sm'
                }`} 
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

